import { useEffect, useState } from 'react';

export function HeroMapSoundCity() {
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(mediaQuery.matches);

    const handler = (e: MediaQueryListEvent) => setReducedMotion(e.matches);
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  const particles = Array.from({ length: 40 }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: Math.random() * 100,
    size: Math.random() * 1.5 + 0.5,
    delay: Math.random() * 4,
    duration: Math.random() * 6 + 4,
  }));

  return (
    <div className="hero-map-bg relative w-full min-h-[320px] md:min-h-[420px] overflow-hidden ring-1 ring-white/5 shadow-2xl">
      <style>{`
        @keyframes zonePulse1 {
          0%, 100% { opacity: 0.15; transform: scale(1); }
          50% { opacity: 0.3; transform: scale(1.05); }
        }
        @keyframes zonePulse2 {
          0%, 100% { opacity: 0.12; transform: scale(1); }
          50% { opacity: 0.28; transform: scale(1.08); }
        }
        @keyframes zonePulse3 {
          0%, 100% { opacity: 0.18; transform: scale(1); }
          50% { opacity: 0.32; transform: scale(1.06); }
        }
        @keyframes wavePulse {
          0%, 100% { opacity: 0.5; }
          50% { opacity: 0.8; }
        }
        @keyframes particleDrift {
          0% { transform: translate(0, 0); }
          50% { transform: translate(var(--drift-x), var(--drift-y)); }
          100% { transform: translate(0, 0); }
        }
        @keyframes shimmer {
          0%, 100% { opacity: 0.4; }
          50% { opacity: 0.7; }
        }

        @media (prefers-reduced-motion: reduce) {
          .zone-pulse, .wave-pulse, .particle-drift, .shimmer {
            animation: none !important;
          }
        }

        .zone-pulse-1 { animation: zonePulse1 2.5s ease-in-out infinite; }
        .zone-pulse-2 { animation: zonePulse2 3s ease-in-out infinite; }
        .zone-pulse-3 { animation: zonePulse3 2.8s ease-in-out infinite; }
        .wave-pulse { animation: wavePulse 4s ease-in-out infinite; }
        .shimmer { animation: shimmer 3s ease-in-out infinite; }
      `}</style>

      <svg
        className="w-full h-full"
        viewBox="0 0 1000 600"
        xmlns="http://www.w3.org/2000/svg"
        preserveAspectRatio="xMidYMid slice"
      >
        <defs>
          <radialGradient id="turquoiseGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#00c7b7" stopOpacity="0.4" />
            <stop offset="50%" stopColor="#00c7b7" stopOpacity="0.2" />
            <stop offset="100%" stopColor="#00c7b7" stopOpacity="0" />
          </radialGradient>

          <filter id="glow">
            <feGaussianBlur stdDeviation="3" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          <linearGradient id="trailGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#00c7b7" stopOpacity="0.1" />
            <stop offset="50%" stopColor="#00c7b7" stopOpacity="0.7" />
            <stop offset="100%" stopColor="#14f0d5" stopOpacity="0.3" />
          </linearGradient>
        </defs>

        {/* Transparent background to show CSS map below */}
        <rect width="100%" height="100%" fill="transparent" />

        {/* Sound Zone 1 - Large central zone */}
        <g className={reducedMotion ? '' : 'zone-pulse-1'}>
          <circle
            cx="500"
            cy="300"
            r="120"
            fill="url(#turquoiseGlow)"
            opacity="0.15"
          />
          <circle
            cx="500"
            cy="300"
            r="120"
            fill="none"
            stroke="#00c7b7"
            strokeWidth="2"
            opacity="0.3"
            strokeDasharray="8 4"
            className={reducedMotion ? '' : 'shimmer'}
          />
        </g>

        {/* Sound Zone 2 - Upper left */}
        <g className={reducedMotion ? '' : 'zone-pulse-2'}>
          <circle
            cx="250"
            cy="150"
            r="90"
            fill="url(#turquoiseGlow)"
            opacity="0.12"
          />
          <circle
            cx="250"
            cy="150"
            r="90"
            fill="none"
            stroke="#00c7b7"
            strokeWidth="2"
            opacity="0.25"
            strokeDasharray="6 3"
          />
        </g>

        {/* Sound Zone 3 - Upper right */}
        <g className={reducedMotion ? '' : 'zone-pulse-3'}>
          <circle
            cx="750"
            cy="180"
            r="100"
            fill="url(#turquoiseGlow)"
            opacity="0.18"
          />
          <circle
            cx="750"
            cy="180"
            r="100"
            fill="none"
            stroke="#00c7b7"
            strokeWidth="2"
            opacity="0.28"
            strokeDasharray="8 4"
          />
        </g>

        {/* Sound Zone 4 - Lower left */}
        <g className={reducedMotion ? '' : 'zone-pulse-1'} style={{ animationDelay: '0.8s' }}>
          <circle
            cx="300"
            cy="450"
            r="80"
            fill="url(#turquoiseGlow)"
            opacity="0.14"
          />
          <circle
            cx="300"
            cy="450"
            r="80"
            fill="none"
            stroke="#00c7b7"
            strokeWidth="2"
            opacity="0.26"
            strokeDasharray="5 3"
          />
        </g>

        {/* Sound Zone 5 - Lower right */}
        <g className={reducedMotion ? '' : 'zone-pulse-2'} style={{ animationDelay: '1.2s' }}>
          <circle
            cx="700"
            cy="420"
            r="95"
            fill="url(#turquoiseGlow)"
            opacity="0.16"
          />
          <circle
            cx="700"
            cy="420"
            r="95"
            fill="none"
            stroke="#00c7b7"
            strokeWidth="2"
            opacity="0.27"
            strokeDasharray="7 3"
          />
        </g>

        {/* Sound Trail - Curved path connecting zones */}
        <g className={reducedMotion ? '' : 'wave-pulse'}>
          <path
            d="M 250 150 Q 350 200, 500 300 T 750 180"
            stroke="url(#trailGradient)"
            strokeWidth="3"
            fill="none"
            filter="url(#glow)"
          />
          <path
            d="M 500 300 Q 600 360, 700 420"
            stroke="url(#trailGradient)"
            strokeWidth="3"
            fill="none"
            filter="url(#glow)"
          />
        </g>

        {/* Pin markers at zone centers */}
        <g filter="url(#glow)">
          <circle cx="250" cy="150" r="5" fill="#00c7b7" />
          <circle cx="500" cy="300" r="6" fill="#14f0d5" />
          <circle cx="750" cy="180" r="5" fill="#00c7b7" />
          <circle cx="300" cy="450" r="4" fill="#00c7b7" />
          <circle cx="700" cy="420" r="5" fill="#00c7b7" />
        </g>

        {/* Particle scatter (buildings/lights) */}
        {!reducedMotion && particles.map((particle) => (
          <circle
            key={particle.id}
            cx={`${particle.x}%`}
            cy={`${particle.y}%`}
            r={particle.size}
            fill={particle.size > 1 ? '#00c7b7' : '#ffffff'}
            opacity={particle.size > 1 ? '0.4' : '0.6'}
            style={{
              animation: `particleDrift ${particle.duration}s ease-in-out infinite`,
              animationDelay: `${particle.delay}s`,
              '--drift-x': `${(Math.random() - 0.5) * 20}px`,
              '--drift-y': `${(Math.random() - 0.5) * 20}px`,
            } as React.CSSProperties}
          />
        ))}

        {/* Additional static particles for reduced motion */}
        {reducedMotion && particles.slice(0, 20).map((particle) => (
          <circle
            key={particle.id}
            cx={`${particle.x}%`}
            cy={`${particle.y}%`}
            r={particle.size}
            fill={particle.size > 1 ? '#00c7b7' : '#ffffff'}
            opacity={particle.size > 1 ? '0.4' : '0.6'}
          />
        ))}

      </svg>
    </div>
  );
}
